import type { ButtonProps } from '@gitbook/spine-react';

/**
 * Base definition of an action returned by getUpgradeAction.
 * It's a set of ButtonProps with a discriminator and a less strict onPress event handler.
 */
export interface UseUpgradeAction extends ButtonProps {
    /** discriminator for type narrowing */
    _type:
        | 'upgrade'
        | 'site-upgrade'
        | 'contact-support'
        | 'contact-sales'
        | 'ai-disabled'
        | 'ai-restricted'
        | 'ai-paused';

    /** Label to display on the upgrade CTA. */
    label: string;

    /** Optional label to display on upgrade CTA badges, defaults to the button label */
    badgeLabel?: string;

    /** upgrade actions onPress doesn't need to require an event */
    onPress: () => Promise<void>;
}

/**
 * Organization-level action: upgrade to a core plan, contact sales, or enable AI.
 */
export interface UpgradeAction extends UseUpgradeAction {
    _type: 'upgrade' | 'contact-sales' | 'ai-disabled' | 'ai-restricted' | 'ai-paused';

    /** The required plan for the given feature key */
    plan?: string;
}

/**
 * Action to enable AI on the organization, or to ask an administrator to.
 */
export type AIDisabledAction = UpgradeAction & { _type: 'ai-disabled' };

/** Action to manage the credits that automatically paused AI. */
export type AIPausedAction = UpgradeAction & { _type: 'ai-paused' };

/** AI can't be enabled for the organization, so there is nothing to act on. */
export type AIRestrictedAction = UpgradeAction & { _type: 'ai-restricted' };

/**
 * Action to prompt the user to upgrade to a site plan.
 */
export interface SiteUpgradeAction extends UseUpgradeAction {
    _type: 'site-upgrade';

    /** The required plan for the given feature key */
    plan: string;
}

/**
 * Action to to prompt the user to contact support.
 */
export interface ContactSupportAction extends UseUpgradeAction {
    _type: 'contact-support';
}

export type FeatureUpgradeAction = UpgradeAction | SiteUpgradeAction | ContactSupportAction;

/**
 * Action type guards
 */
export function isUpgradeAction(action?: UseUpgradeAction): action is UpgradeAction {
    return action?._type === 'upgrade';
}
export function isSiteUpgradeAction(
    action: UseUpgradeAction | null | undefined
): action is SiteUpgradeAction {
    return action?._type === 'site-upgrade';
}
export function isAIDisabledAction(action?: UseUpgradeAction): action is AIDisabledAction {
    return action?._type === 'ai-disabled';
}

export function isAIPausedAction(action?: UseUpgradeAction): action is AIPausedAction {
    return action?._type === 'ai-paused';
}

export function isAIRestrictedAction(action?: UseUpgradeAction): action is AIRestrictedAction {
    return action?._type === 'ai-restricted';
}

/** Check whether AI settings, a restriction or a credit pause prevent access. */
export function isAIUnavailableAction(
    action?: UseUpgradeAction
): action is AIDisabledAction | AIRestrictedAction | AIPausedAction {
    return isAIDisabledAction(action) || isAIRestrictedAction(action) || isAIPausedAction(action);
}
